import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {HttpClient} from '@angular/common/http';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {first} from 'rxjs/operators';
import {CommissionJob} from '../interface/commission-job';
import {Uuid} from "../interface/response/uuid";

@Injectable({
  providedIn: 'root'
})
export class CommissionJobService {
  public readonly jobListSubject: ReplaySubject<CountedAndPaginated<CommissionJob>>
    = new ReplaySubject<CountedAndPaginated<CommissionJob>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public loadJobList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/commission-job?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<CommissionJob>) => {
        this.jobListSubject.next(result);
      });
  }

  public getJobList(
    page: number,
    pageSize: number,
    filters: object
  ): Observable<CountedAndPaginated<CommissionJob>> {
    return this.httpClient.get<CountedAndPaginated<CommissionJob>>(
      `/rest/crm/commission-job?page=${page + 1}&pageSize=${pageSize}&` +
      this.filterService.parseFilters(filters)
    );
  }

  structureUpdate(formData: any): Observable<any> {
    const formDataFormatted = {...formData};
    formDataFormatted.billingMonth = formDataFormatted.billingMonth.format('YYYY-MM');

    return this.httpClient.post(`/rest/crm/commission-job/job/structure-update`, formDataFormatted)
      .pipe(first());
  }

  ratePaidMatch(formData: any): Observable<any> {
    const formDataFormatted = {...formData};
    formDataFormatted.billingMonth = formDataFormatted.billingMonth.format('YYYY-MM');

    return this.httpClient.post(`/rest/crm/commission-job/job/rate-paid-match`, formDataFormatted)
      .pipe(first());
  }

  generateSettlements(formData: any): Observable<any> {
    const formDataFormatted = {...formData};
    formDataFormatted.billingMonth = formDataFormatted.billingMonth.format('YYYY-MM');

    return this.httpClient.post(`/rest/crm/commission-job/job/generate-settlements`, formDataFormatted)
      .pipe(first());
  }

  generateInvoices(formData: any): Observable<any> {
    const formDataFormatted = {...formData};
    formDataFormatted.billingMonth = formDataFormatted.billingMonth.format('YYYY-MM');

    return this.httpClient.post(`/rest/crm/commission-job/job/generate-invoices`, formDataFormatted)
      .pipe(first());
  }

  generateCommissionStatements(formData: any): Observable<any> {
    const formDataFormatted = {...formData};
    formDataFormatted.billingMonth = formDataFormatted.billingMonth.format('YYYY-MM');

    return this.httpClient.post(`/rest/crm/commission-job/job/generate-commission-statements`, formDataFormatted)
      .pipe(first());
  }

  closeMonth(formData: any): Observable<any> {
    const formDataFormatted = {...formData};
    formDataFormatted.billingMonth = formDataFormatted.billingMonth.format('YYYY-MM');

    return this.httpClient.post(`/rest/crm/commission-job/job/close-month`, formDataFormatted)
      .pipe(first());
  }

  sendSettlementsToPartners(formData: any): Observable<any> {
    const formDataFormatted = {...formData};
    formDataFormatted.billingMonth = formDataFormatted.billingMonth.format('YYYY-MM');

    return this.httpClient.post(`/rest/crm/commission-job/job/send-settlements-to-partners`, formDataFormatted)
      .pipe(first());
  }

  deleteJob(jobId: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/commission-job/${jobId}`)
      .pipe(first());
  }
  zipDownload(jobId: string): Observable<Uuid> {
    return this.httpClient.get(`/rest/crm/commission-job/${jobId}/zip-download`, {})
      .pipe(first()) as Observable<Uuid>;
  }
  public downloadFile(jobId: string): void {
    this.document.location.href = `/rest/crm/commission-job/${jobId}/downloadReportFile`;
  }

  public downloadGroupedCommissionStatementsPdf(jobId: string) {
    return this.httpClient.post<Uuid>(
      `/rest/crm/commission-job/${jobId}/grouped-commission-statements-pdf`,
      {}
    );
  }

  public downloadGroupedCommissionStatementsPdfFile(jobId: string): void {
    this.document.location.href = `/rest/crm/commission-job/${jobId}/grouped-commission-statements-pdf/download`;
  }

  deleteJobInvoice(jobId: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/commission-job-invoice/${jobId}`, {})
      .pipe(first());
  }

  downloadFileFromResult(jobUuid: string, code: string): void {
    this.document.location.href = `/rest/crm/commission-job/${jobUuid}/result-file/${code}`;
  }
}
